import { useState, useRef } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { MinimalInput } from '@/components/ui/MinimalInput'
import { AlertCircle, Loader2, Eye, EyeOff } from 'lucide-react'

const theseusLogo = '/theseus-logo.png'

function safeNextPath(value: string | null): string {
  return value?.startsWith('/') && !value.startsWith('//') ? value : '/'
}

export function LoginForm() {
  const { signIn, user } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const passwordRef = useRef<HTMLInputElement>(null)

  const redirectTo = safeNextPath(searchParams.get('next'))

  if (user) {
    return <Navigate to={redirectTo} replace />
  }

  const canSubmit = email.trim() !== '' && password.trim() !== ''

  const handleSubmit = async () => {
    setError(null)

    if (!email || !password) {
      setError('Please enter your email and password')
      return
    }

    setSubmitting(true)

    try {
      await signIn(email, password)
      navigate(redirectTo)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid email or password')
    } finally {
      setSubmitting(false)
    }
  }

  const focusPassword = () => {
    passwordRef.current?.focus()
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Logo - top left */}
      <a
        href="https://theseus.us"
        target="_blank"
        rel="noopener noreferrer"
        className="fixed top-4 left-4 md:top-6 md:left-6 hover:opacity-80 transition-opacity z-10"
      >
        <img src={theseusLogo} alt="Theseus" className="h-5 md:h-6" />
      </a>

      {/* Centered content */}
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-md">
          {/* Header */}
          <div className="mb-10 wizard-fade-in">
            <h1 className="text-2xl text-foreground">Sign in</h1>
          </div>

          <form
            onSubmit={(e) => {
              e.preventDefault()
              handleSubmit()
            }}
            className="space-y-6"
          >
            <div style={{ animationDelay: '50ms' }}>
              <MinimalInput
                label="Email"
                name="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onEnterPress={focusPassword}
                required
              />
            </div>

            <div style={{ animationDelay: '100ms' }} className="space-y-2 wizard-stagger">
              <div className="flex justify-between items-center">
                <label className="block text-sm text-muted-foreground/80">Password</label>
                <Link to="/forgot-password" className="text-xs text-muted-foreground hover:text-foreground">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <input
                  ref={passwordRef}
                  className="ph-no-capture w-full bg-transparent border-b border-border/40 pb-2 pr-10 text-foreground text-base
                    placeholder:text-muted-foreground/40 focus:outline-none focus:border-foreground/60
                    transition-colors"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="current-password"
                  placeholder="Your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && canSubmit) {
                      e.preventDefault()
                      handleSubmit()
                    }
                  }}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 top-0 p-1 text-muted-foreground hover:text-foreground transition-colors"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Error Display */}
            {error && (
              <div className="flex items-center gap-2 text-sm text-destructive wizard-fade-in">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {/* Submit Button */}
            <div className="pt-4">
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full px-6 py-2.5 rounded-md text-sm font-medium transition-all
                  bg-primary text-primary-foreground hover:bg-primary/90
                  disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <span className="flex items-center justify-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Signing in...
                  </span>
                ) : (
                  <>
                    Sign in
                    <span className="text-primary-foreground/60 ml-2 text-xs">↵</span>
                  </>
                )}
              </button>
            </div>

            {/* Invite-only access */}
            <p className="text-center text-sm text-muted-foreground pt-2">
              Need access?{' '}
              <a
                href="mailto:support@theseus.us?subject=Theseus%20access%20request"
                className="text-primary hover:underline"
              >
                Contact Theseus
              </a>
            </p>
          </form>

          {/* Keyboard hint */}
          <div className="mt-8 text-center text-[10px] text-muted-foreground/40">
            <span className="px-1.5 py-0.5 bg-secondary/50 rounded">Enter</span> to continue
          </div>
        </div>
      </main>
    </div>
  )
}
